import React, { FC, useState, CSSProperties } from 'react';

import { Popover, PopoverProps } from '@mui/material';

// components
import DropdownOption from '../../../components/nav/DrowpdownMenu/DropdownOption';
import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';
import Icons from '../../../constants/icons';

import BasicButton from '../../buttons/basicButton';

export interface OptionModel {
    name: string;
    isLink?: boolean;
    url?: string;
    isDisabled?: boolean;
    tooltipText?: string;
    textColor?: string;
    action?: () => void;
    submenuOptions?: OptionModel[];
    isTrailingIcon?: boolean;
    icon?: any;
    textStyle?: React.CSSProperties;
}

interface DropdownMenuProps {
    options: OptionModel[];
    isVertical?: boolean;
    style?: CSSProperties; // style prop for Popover styles if needed
    menuStyle?: CSSProperties; // style prop for MenuContainer
    wrapperStyle?: CSSProperties;
    buttonConfig?: {
        buttonText?: string;
        isTrailingIcon?: boolean;
        menuOpenIcon?: any;
        menuCloseIcon?: any;
        isPrimary?: boolean;
    };
    submenuPosition?: 'left' | 'right';
}

const DropdownMenu: FC<DropdownMenuProps> = ({
    options,
    isVertical,
    style,
    menuStyle,
    buttonConfig,
    wrapperStyle,
    submenuPosition = 'right',
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | SVGElement | null>(
        null,
    );

    const toggleMenu = (event?: React.MouseEvent<HTMLElement | SVGElement>) => {
        event?.stopPropagation();
        setAnchorEl(anchorEl ? null : event?.currentTarget || null);
    };

    const handleAction = (action: () => void) => {
        action();
        toggleMenu();
    };

    const handlePopoverClose: PopoverProps['onClose'] = (event) => {
        const e = event as Event;
        e?.stopPropagation?.();
        setAnchorEl(null);
    };

    return (
        <>
            {buttonConfig?.buttonText ? (
                <BasicButton
                    isPrimary={buttonConfig.isPrimary}
                    onClick={toggleMenu}
                    name={buttonConfig.buttonText}
                    icon={
                        anchorEl
                            ? buttonConfig.menuCloseIcon
                            : buttonConfig.menuOpenIcon
                    }
                    isTrailingIcon={buttonConfig.isTrailingIcon}
                    data-testid="dropdown-button-text"
                />
            ) : (
                <IconWrapper
                    as={isVertical ? Icons.verticalMore : Icons.more}
                    onClick={toggleMenu}
                    style={wrapperStyle}
                    data-testid="dropdown-button-icon"
                />
            )}
            <Popover
                className="drawer-click-exempt"
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: submenuPosition === 'right' ? 'left' : 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: submenuPosition === 'right' ? 'left' : 'right',
                }}
                style={style}
            >
                <MenuContainer style={menuStyle}>
                    {options.map((option) => (
                        <DropdownOption
                            key={option.name}
                            option={option}
                            handleAction={handleAction}
                            submenuOptions={option.submenuOptions}
                            submenuPosition={submenuPosition}
                        />
                    ))}
                </MenuContainer>
            </Popover>
        </>
    );
};

export default DropdownMenu;

// Styled Components
const IconWrapper = styled.div({
    cursor: 'pointer',
    color: aampeTheme.colors.textAccessibleWG01,
    height: 24,
    width: 24,
    '&:hover': {
        color: aampeTheme.colors.solidA01,
    },
});

const MenuContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
});
